/**
 * A key, drawn like a key, and a combination of them.
 *
 * Two sizes because the two places a cap appears want different weights. The
 * shortcuts drawer lists bindings, and there the cap is the thing being read,
 * 25 by 28 on the selected grey. Inside a search field the `/` cap is a hint
 * beside a placeholder and must not outshout it, so it is 20 square on the
 * muted grey in the 11px type.
 *
 * `Mod` is resolved here, once, so no call site spells a platform. Apple
 * platforms get the command glyph and everything else gets `Ctrl`, which is the
 * same rule `modLabel` already applies to the sidebar's hint.
 */
import type { ReactNode } from 'react'
import { cn } from '../lib/cn'
import { modLabel } from './sidebar-model'

export type KeycapSize = 'default' | 'inline'

const SIZES: Record<KeycapSize, string> = {
  default: 'h-7 min-w-[25px] px-1.5 bg-surface-selected text-xs text-ink-secondary',
  inline: 'h-5 min-w-5 px-1 bg-surface-muted text-2xs text-ink-subtle',
}

export interface KeycapProps {
  children: ReactNode
  size?: KeycapSize
  className?: string
}

/** One key. A `<kbd>`, so a screen reader announces it as keyboard input. */
export function Keycap({ children, size = 'default', className }: KeycapProps) {
  return (
    <kbd
      data-numeric=""
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded border border-line font-sans font-medium',
        SIZES[size],
        className,
      )}
    >
      {children}
    </kbd>
  )
}

/** What a key is called on a cap. `Mod` becomes the platform's modifier, the rest pass through. */
export function capLabel(key: string, platform: string | null | undefined): string {
  if (key.toLowerCase() === 'mod') return modLabel(platform).trim()
  if (key === 'Enter') return '↵'
  if (key === 'Escape') return 'esc'
  return key.length === 1 ? key.toUpperCase() : key
}

/** `'Mod+O'` or `'mod+k'` as a list of caps. A lone `+` is the plus key, not a joiner. */
export function comboKeys(combo: string): string[] {
  if (combo === '+') return ['+']
  return combo
    .split('+')
    .map((part) => part.trim())
    .filter((part) => part !== '')
}

function currentPlatform(): string | undefined {
  if (typeof navigator === 'undefined') return undefined
  return navigator.platform || navigator.userAgent
}

export interface KeyComboProps {
  /** `'Mod+O'`, `'Shift+?'`, or a single key. */
  combo: string
  size?: KeycapSize
  className?: string
  /** For tests and previews. Read from the browser when left out. */
  platform?: string | null
  /**
   * The whole chord inside ONE cap, `⌘+O`, rather than a cap per key. Right
   * beside a single row it operates, such as the sidebar's Shortcuts link,
   * where two small boxes read as two suggestions. A list of shortcuts keeps
   * a cap per key, so the column lines up key by key.
   */
  joined?: boolean
}

/** One cap per key, joined by a plus in the subtle ink: `⌘` `+` `O`. */
export function KeyCombo({ combo, size = 'default', className, platform, joined = false }: KeyComboProps) {
  const resolved = platform === undefined ? currentPlatform() : platform
  const keys = comboKeys(combo)
  if (joined) {
    return (
      <Keycap size={size} className={className}>
        {keys.map((key) => capLabel(key, resolved)).join('+')}
      </Keycap>
    )
  }
  return (
    <span className={cn('inline-flex shrink-0 items-center gap-1', className)}>
      {keys.map((key, index) => (
        <span key={`${key}-${index}`} className="inline-flex items-center gap-1">
          {index > 0 ? (
            <span aria-hidden="true" className="text-xs text-ink-subtle">
              +
            </span>
          ) : null}
          <Keycap size={size}>{capLabel(key, resolved)}</Keycap>
        </span>
      ))}
    </span>
  )
}
